













































import Vue from 'vue';
import _ from 'lodash';
import { sanityClient } from '@/config/sanity';
import Story from '@/models/story';
import Employee from '@/models/employee';
import Customer from '@/models/customer';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import ErrorMessage from '@/components/ErrorMessage.vue';
import CustomersPanel from '@/components/CustomersPanel.vue';
import StoriesPanel from '@/components/StoriesPanel.vue';
import TextWithPeoplePanel from '@/components/TextWithPeoplePanel.vue';
import EmployeesPanel from '@/components/EmployeesPanel.vue';
import SpeakerWithGradient from '@/components/SpeakerWithGradient.vue';

interface TechPage {
  introductionPanel: {
    headline: string;
    text: string;
    by: Array<Employee>;
  };
  recruitmentPanel: {
    headline: string;
    text: string;
    contactPersons: Array<Employee>;
  };
  customersPanel: {
    headline: string;
  };
  employeesPanel: {
    headline: string;
  };
}

interface Data {
  isLoading: boolean;
  error: string;
  techPage: TechPage | null;
  stories: Array<Story>;
  employees: Array<Employee>;
  customers: Array<Customer>;
}

export default Vue.extend({
  name: 'Tech',
  components: {
    LoadingIndicator,
    ErrorMessage,
    TextWithPeoplePanel,
    StoriesPanel,
    EmployeesPanel,
    CustomersPanel,
    SpeakerWithGradient,
  },
  data(): Data {
    return {
      isLoading: true,
      error: '',
      techPage: null,
      stories: [],
      employees: [],
      customers: [],
    };
  },
  created() {
    this.fetchTechPage();
  },
  methods: {
    fetchTechCustomers() {
      const query = `*[_type == "customer" && "technology" in department]{
        _id,
        name,
        logo,
      }`;

      return sanityClient.fetch(query).then(
        (results) => {
          this.customers = results;
        },
        (error) => {
          this.error = error;
        },
      );
    },
    fetchTechStories() {
      const query = `*[_type == "story" && department == "technology"] | order(_createdAt desc) {
        "createdAt": _createdAt,
        "updatedAt": _updatedAt,
        "id": _id,
        "key": _key,
        "slug": slug.current,
        title,
        excerpt,
        image,
        author,
        department,
        publishedDate,
        updatedDate
      }[0...4]`;

      return sanityClient.fetch(query).then(
        (results) => {
          this.stories = results;
        },
        (error) => {
          this.error = error;
        },
      );
    },
    fetchTechEmployees() {
      const query = `*[_type == "employee"]{
        _createdAt,
        _updatedAt,
        name,
        slug,
        title,
        department,
        image,
        order,
        person->{name,slug,image,title,department,order},
      }`;

      return sanityClient.fetch(query).then(
        (results) => {
          const techEmployees = results.filter((r: Record<string, any>) => {
            if (r.department) {
              return r.department === 'technology';
            }
            if (r.person && r.person.department) {
              return r.person.department === 'technology';
            }
            return false;
          });
          const normalizedEmployees = techEmployees.map((e: Record<string, any>) => {
            if (e.person) {
              return {
                name: e.person.name,
                title: e.person.title,
                department: e.person.department,
                image: e.person.image,
                order: e.person.order,
              };
            }
            return {
              name: e.name,
              title: e.title,
              department: e.department,
              image: e.image,
              order: e.order,
            };
          });
          const sortedEmployees = _.sortBy(normalizedEmployees, 'order', 'asc');
          this.employees = sortedEmployees;
        },
        (error) => {
          this.error = error;
        },
      );
    },
    fetchTechPageFields() {
      const query = `*[_type == "techPage"]{
        _createdAt,
        _updatedAt,
        introductionPanelHeadline,
        introductionText,
        introductionBy->{name,title,image,department},
        recruitmentPanelHeadline,
        recruitmentText,
        recruitmentContactPersons[]->{name,title,image,department},
        customersPanelHeadline,
        employeesPanelHeadline,
        storiesPanelHeadline,
      }`;

      return sanityClient.fetch(query).then(
        (results) => {
          const page = results[0];
          this.techPage = {
            introductionPanel: {
              headline: page.introductionPanelHeadline,
              text: page.introductionText,
              by: page.introductionBy ? [page.introductionBy] : [],
            },
            recruitmentPanel: {
              headline: page.recruitmentPanelHeadline,
              text: page.recruitmentText,
              contactPersons: page.recruitmentContactPersons,
            },
            customersPanel: {
              headline: page.customersPanelHeadline,
            },
            employeesPanel: {
              headline: page.employeesPanelHeadline,
            },
          };
        },
        (error) => {
          this.error = error;
        },
      );
    },
    fetchTechPage() {
      this.isLoading = true;
      Promise.all([
        this.fetchTechPageFields(),
        this.fetchTechEmployees(),
        this.fetchTechStories(),
        this.fetchTechCustomers(),
      ]).then(() => {
        this.isLoading = false;
      }).catch(() => {
        this.isLoading = false;
      });
    },
  },
});
